import { create } from 'zustand';
import type {
  ClientIntent,
  NarrationMessage,
  PrivateView,
  PublicView,
  ServerMessage,
  TimerMessage,
} from '@ringmaster/shared';
import type { ConnectOptions, GameDriver } from '../driver/GameDriver.js';
import { saveSession, clearSession } from './session.js';
import { SITTING_LOG_MAX, sittingGameFrom, type SittingGame } from './sitting.js';

/**
 * useRoom — the driver-agnostic client store. FULLY IMPLEMENTED in Wave 0: it
 * ingests ServerMessages from any GameDriver (Mock or LiveSocket) and exposes the
 * latest public/private view, narration caption, and timer to the UI. Only the
 * socket transport is stubbed (net/socket.ts) — this store never needs changing.
 *
 * Usage:
 *   useRoom.getState().attach(new MockDriver(), { role: 'hand', code: 'RVNK' });
 *   const view = useRoom(s => s.publicView);
 */

/** The single narration caption currently on screen. */
export interface NarrationState {
  audioId: string | null;
  text: string;
  captionOnly: boolean;
}

export interface TimerState {
  phase: TimerMessage['phase'];
  endsAt: number;
}

export interface RoomState {
  // Connection identity
  driver: GameDriver | null;
  connected: boolean;
  playerId: string | null;
  sessionToken: string | null;
  code: string | null;
  isHost: boolean;

  // Latest server-pushed state
  publicView: PublicView | null;
  privateView: PrivateView | null;
  narration: NarrationState | null;
  timer: TimerState | null;
  lastError: { code: string; message: string } | null;
  /**
   * The room this client was playing in no longer exists server-side — the
   * process restarted (a deploy; rooms are in-memory) or the room was swept.
   * Set from an ERROR the server sends on shutdown, or from a ROOM_NOT_FOUND
   * answer to the driver's silent auto-REJOIN.
   *
   * Without this the UI has no idea: the socket just goes quiet, the phase
   * timer keeps counting down locally to 00:00, buttons post into the void and
   * the narrator falls silent with the last caption still on screen. Surfaces
   * are expected to READ this and recover (the Activity re-boots its session).
   */
  roomLost: boolean;
  /**
   * Every game this client has watched THIS room finish, this visit, oldest
   * first (see net/sitting.ts). Appended on the public transition INTO END —
   * never on a re-broadcast of the same END, so a reconnect or an in-phase
   * no-op cannot inflate it — and emptied when the client joins a different
   * room. `length` is the sitting's game count, which is what gates the END
   * share strip (ui/NextShowStrip.tsx).
   */
  sitting: SittingGame[];

  // Actions
  attach: (driver: GameDriver, opts: ConnectOptions) => void;
  send: (intent: ClientIntent) => void;
  ingest: (msg: ServerMessage) => void;
  detach: () => void;
  reset: () => void;
}

const initialState = {
  driver: null as GameDriver | null,
  connected: false,
  playerId: null as string | null,
  sessionToken: null as string | null,
  code: null as string | null,
  isHost: false,
  publicView: null as PublicView | null,
  privateView: null as PrivateView | null,
  narration: null as NarrationState | null,
  timer: null as TimerState | null,
  lastError: null as { code: string; message: string } | null,
  roomLost: false,
  sitting: [] as SittingGame[],
};

export const useRoom = create<RoomState>((set, get) => {
  let unsubscribe: (() => void) | null = null;

  return {
    ...initialState,

    attach(driver, opts) {
      // Tear down any prior driver subscription.
      unsubscribe?.();
      const off = driver.onMessage((msg) => get().ingest(msg));
      unsubscribe = off;
      set({ driver, connected: true, lastError: null });
      void driver.connect(opts);
    },

    send(intent) {
      get().driver?.send(intent);
    },

    ingest(msg) {
      switch (msg.type) {
        case 'JOINED': {
          // A different room is a different sitting. A RE-join of the same code
          // (reconnect, host handoff) keeps the log — the games were still
          // played by this guild, in this room, on this visit.
          if (get().code !== null && get().code !== msg.code) set({ sitting: [] });
          set({
            playerId: msg.playerId,
            sessionToken: msg.sessionToken,
            code: msg.code,
            isHost: msg.isHost,
          });
          saveSession({
            code: msg.code,
            playerId: msg.playerId,
            sessionToken: msg.sessionToken,
          });
          break;
        }
        case 'PUBLIC_STATE': {
          // Re-derive host-ness from the live view so a server-side host handoff
          // (original host left) moves the crown here without a fresh JOINED. The
          // JOINED boolean seeds it; this keeps it current. playerId is set by the
          // earlier JOINED, so this is null-safe (false until we know our id).
          //
          // The sitting log grows on the EDGE into END only: END re-broadcasts
          // (a reconnect, a late award patch) must not count a second game.
          const wasEnd = get().publicView?.phase === 'END';
          // Stamped here rather than inside `sittingGameFrom`, which stays pure:
          // the EDGE into END is the only moment the client knows a game has
          // just finished, and the END surface needs that instant to read the
          // weekly run correctly (net/sitting.ts, ui/guildStreak.ts).
          const logged = msg.view.phase === 'END' && !wasEnd ? sittingGameFrom(msg.view) : null;
          const record = logged ? { ...logged, endedAt: Date.now() } : null;
          set({
            publicView: msg.view,
            isHost: msg.view.hostId === get().playerId,
            ...(record ? { sitting: [...get().sitting, record].slice(-SITTING_LOG_MAX) } : {}),
          });
          break;
        }
        case 'PRIVATE_STATE': {
          set({ privateView: msg.view });
          break;
        }
        case 'NARRATION': {
          const n = msg as NarrationMessage;
          set({
            narration: { audioId: n.audioId, text: n.text, captionOnly: n.captionOnly },
          });
          break;
        }
        case 'TIMER': {
          set({ timer: { phase: msg.phase, endsAt: msg.endsAt } });
          break;
        }
        case 'ERROR': {
          // A kick (host removed us) or a dead session (auto-REJOIN with a
          // stale token) both mean "you're no longer seated" — forget the
          // stored session and drop our identity so the route falls back to the
          // join screen. Keep the driver ATTACHED so the same socket can send a
          // fresh JOIN_ROOM; a full reset() would strand the join form.
          // The room is GONE (server restarted / swept) — not "you were
          // refused", but "there is nothing to be refused by". Flag it so the
          // surface can say so and recover; keep the last views on screen so
          // the recovery UI can render over them instead of a black flash.
          // ROOM_NOT_FOUND only means this when we HAD a room: the same code
          // answers a mistyped join code, which must keep showing the form.
          if (msg.code === 'SERVER_RESTARTING' || (msg.code === 'ROOM_NOT_FOUND' && get().publicView !== null)) {
            set({ roomLost: true, lastError: { code: msg.code, message: msg.message } });
            break;
          }
          if (msg.code === 'KICKED' || msg.code === 'SESSION_INVALID') {
            const code = get().code;
            if (code) clearSession(code);
            set({
              playerId: null,
              sessionToken: null,
              code: null,
              isHost: false,
              publicView: null,
              privateView: null,
              narration: null,
              timer: null,
              lastError: { code: msg.code, message: msg.message },
              // No longer seated: this sitting is over for us. Clearing here
              // (not only on a different JOINED code) is what keeps a rejoin
              // into a FRESH room from inheriting the old room's games —
              // `code` is nulled above, so the join-time guard cannot see it.
              sitting: [],
            });
            break;
          }
          set({ lastError: { code: msg.code, message: msg.message } });
          break;
        }
        default: {
          const _exhaustive: never = msg;
          void _exhaustive;
        }
      }
    },

    detach() {
      unsubscribe?.();
      unsubscribe = null;
      get().driver?.close();
      set({ driver: null, connected: false });
    },

    reset() {
      unsubscribe?.();
      unsubscribe = null;
      get().driver?.close();
      set({ ...initialState });
    },
  };
});
